import React, { useEffect, useMemo, useState } from "react";
import { Search, BookOpen, Users, Plus, GraduationCap, Loader2, Trash2, CheckSquare, Square, Lock } from "lucide-react";
import { ApiService } from "../../../services/api";
import ConfirmModal from "../../../components/modals/ConfirmModal";

interface Teacher {
  id: string;
  planTeacherId?: string;
  firstName?: string;
  lastName?: string;
  name?: string;
  email?: string;
  currentAssignments?: TeacherAssignment[];
  branchAssignments?: Array<{ id: string; lessonId: string; lesson?: Lesson }>;
}

interface Department {
  id: string;
  name: string;
  parentDepartmentId?: string;
  parentName?: string;
}

interface Lesson {
  id: string;
  name: string;
  code?: string;
}

interface TeacherAssignment {
  id: string;
  teacherId: string;
  teacher?: Teacher;
  lessonId: string;
  lesson?: Lesson;
  classId: string;
  class?: { id: string; name: string };
  hours?: number;
}

const TeacherLessonAssignments = () => {
  const [searchTeacher, setSearchTeacher] = useState("");
  const [selectedTeacher, setSelectedTeacher] = useState<string | null>(null);
  const [selectedLesson, setSelectedLesson] = useState<string | null>(null);
  const [selectedClasses, setSelectedClasses] = useState<string[]>([]);
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [departmentClasses, setDepartmentClasses] = useState<Department[]>([]);
  const [assignments, setAssignments] = useState<TeacherAssignment[]>([]);
  const [allDepartmentsMap, setAllDepartmentsMap] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);
  const [loadingLessonClasses, setLoadingLessonClasses] = useState(false);
  const [lessonClassIds, setLessonClassIds] = useState<string[]>([]);
  const [studentDeptId, setStudentDeptId] = useState<string | null>(null);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [selectedAssignments, setSelectedAssignments] = useState<string[]>([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [bulkDeleteModalOpen, setBulkDeleteModalOpen] = useState(false);
  const [assignmentToDelete, setAssignmentToDelete] = useState<string | null>(null);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (studentDeptId) fetchStructuralDepartments(studentDeptId);
  }, [studentDeptId]);

  useEffect(() => {
    fetchTeachers();
  }, [selectedLesson, searchTeacher]);

  useEffect(() => {
    const fetchLessonClasses = async () => {
      if (!selectedLesson) {
        setLessonClassIds([]);
        setSelectedClasses([]);
        return;
      }

      setLoadingLessonClasses(true);
      try {
        const res = await ApiService.getAssignmentsByLesson(selectedLesson);
        const data = parseListResponse(res);
        const ids = new Set<string>();

        data.forEach((item: any) => {
          if (Array.isArray(item.departmentIds)) {
            item.departmentIds.forEach((id: string) => ids.add(id));
          } else if (item.classId) {
            ids.add(item.classId);
          }
        });

        setLessonClassIds(Array.from(ids));
        setSelectedClasses((prev) => prev.filter((id) => ids.has(id)));
      } catch (error) {
        console.error("Ders sınıfları çekilemedi:", error);
        setLessonClassIds([]);
      } finally {
        setLoadingLessonClasses(false);
      }
    };

    fetchLessonClasses();
  }, [selectedLesson]);

  useEffect(() => {
    fetchAssignments();
  }, [selectedTeacher, selectedLesson]);

  const parseListResponse = (res: any) => {
    const data = (res?.data ?? res) as any;
    if (Array.isArray(data)) return data;
    if (data?.data && Array.isArray(data.data)) return data.data;
    if (data?.results && Array.isArray(data.results)) return data.results;
    return [];
  };

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const settingsRes = await ApiService.getSettings();
      const settings = settingsRes?.data ?? settingsRes;

      await Promise.all([fetchLessons(), fetchAssignments(), fetchTeachers()]);

      const sDeptId = settings?.studentDepartmentId;
      if (sDeptId) setStudentDeptId(sDeptId);
      else console.warn("Öğrenci Departmanı (studentDepartmentId) ayarı bulunamadı.");
    } catch (error) {
      console.error("Veri yükleme hatası:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStructuralDepartments = async (rootId: string) => {
    setLoadingDepartments(true);
    try {
      const categoriesRes = await ApiService.getDepartments(1, 100, `&parentId=${rootId}`);
      const categories = parseListResponse(categoriesRes);

      if (!Array.isArray(categories) || categories.length === 0) {
        setDepartmentClasses([]);
        setLoadingDepartments(false);
        return;
      }

      const map: Record<string, string> = {};
      const leafs: Department[] = [];

      categories.forEach((cat: any) => {
        map[cat.id] = cat.name;
      });

      const results = await Promise.all(
        categories.map((cat: any) =>
          ApiService.getDepartments(1, 100, `&parentId=${cat.id}`)
            .then((res) => {
              const list = parseListResponse(res);
              if (!Array.isArray(list)) return [];
              return list.map((item: any) => ({
                ...item,
                parentDepartmentId: cat.id,
                parentName: cat.name,
              }));
            })
            .catch((error) => {
              console.error(`Kategori ${cat.name} detayları çekilemedi`, error);
              return [];
            })
        )
      );

      results.forEach((list) => {
        list.forEach((cls: Department) => {
          leafs.push(cls);
          map[cls.id] = cls.name;
        });
      });

      setDepartmentClasses(leafs);
      setAllDepartmentsMap(map);
    } catch (error) {
      console.error("Departman yapısı çekilemedi:", error);
      setDepartmentClasses([]);
    } finally {
      setLoadingDepartments(false);
    }
  };

  const fetchTeachers = async () => {
    try {
      const res = await ApiService.getTeacherAssignmentTeachers({
        search: searchTeacher || undefined,
        lessonId: selectedLesson || undefined,
      });
      const nextTeachers = parseListResponse(res);
      setTeachers(nextTeachers);

      if (selectedTeacher && !nextTeachers.some((teacher: Teacher) => teacher.id === selectedTeacher)) {
        setSelectedTeacher(null);
      }
    } catch (error) {
      console.error("Öğretmenler yüklenemedi:", error);
      setTeachers([]);
    }
  };

  const fetchLessons = async () => {
    try {
      const res = await ApiService.getPlanningLessons({ limit: 100, isActive: true });
      setLessons(parseListResponse(res));
    } catch (error) {
      console.error("Dersler yüklenemedi:", error);
      setLessons([]);
    }
  };

  const fetchAssignments = async () => {
    try {
      setAssignmentsLoading(true);
      const res = await ApiService.getTeacherAssignments({
        limit: 100,
        teacherId: selectedTeacher || undefined,
        lessonId: !selectedTeacher ? selectedLesson || undefined : undefined,
      });
      setAssignments(parseListResponse(res));
      setSelectedAssignments([]);
    } catch (error) {
      console.error("Atamalar yüklenemedi:", error);
      setAssignments([]);
    } finally {
      setAssignmentsLoading(false);
    }
  };

  const getTeacherName = (teacher?: Teacher) => {
    if (!teacher) return "İsimsiz";
    if (teacher.firstName && teacher.lastName) return `${teacher.firstName} ${teacher.lastName}`;
    return teacher.name || "İsimsiz";
  };

  const handleClassToggle = (classId: string) => {
    setSelectedClasses((prev) => (
      prev.includes(classId) ? prev.filter((id) => id !== classId) : [...prev, classId]
    ));
  };

  const createAssignment = async () => {
    if (!selectedTeacher || !selectedLesson || selectedClasses.length === 0) return;

    setSubmitting(true);
    try {
      await Promise.all(
        selectedClasses.map((classId) =>
          ApiService.createTeacherAssignment({
            teacherId: selectedTeacher,
            lessonId: selectedLesson,
            classId
          })
        )
      );
      setSelectedClasses([]);
      await Promise.all([fetchAssignments(), fetchTeachers()]);
    } catch (error: any) {
      console.error("Atama oluşturulamadı:", error);
      setErrorMessage(error.response?.data?.message || "Atama oluşturulurken bir hata oluştu.");
      setErrorModalOpen(true);
    } finally {
      setSubmitting(false);
    }
  };

  const confirmRemoveAssignment = async () => {
    if (!assignmentToDelete) return;
    try {
      await ApiService.deleteTeacherAssignment(assignmentToDelete);
      await Promise.all([fetchAssignments(), fetchTeachers()]);
    } catch (error) {
      console.error("Silme hatası:", error);
    } finally {
      setDeleteModalOpen(false);
      setAssignmentToDelete(null);
    }
  };

  const toggleAssignmentSelection = (id: string) => {
    setSelectedAssignments((prev) => (
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    ));
  };

  const visibleAssignments = useMemo(() => assignments, [assignments]);

  const toggleSelectAll = () => {
    if (selectedAssignments.length === visibleAssignments.length) {
      setSelectedAssignments([]);
    } else {
      setSelectedAssignments(visibleAssignments.map((assignment) => assignment.id));
    }
  };

  const confirmBulkDelete = async () => {
    setBulkDeleting(true);
    try {
      await ApiService.bulkDeleteTeacherAssignments(selectedAssignments);
      setSelectedAssignments([]);
      await Promise.all([fetchAssignments(), fetchTeachers()]);
    } catch (error: any) {
      console.error("Toplu silme hatası:", error);
      setErrorMessage(error.response?.data?.message || "Toplu silme sırasında hata oluştu.");
      setErrorModalOpen(true);
    } finally {
      setBulkDeleting(false);
      setBulkDeleteModalOpen(false);
    }
  };

  const getTeacherAssignmentCount = (teacher: Teacher) => {
    return teacher.currentAssignments?.length || 0;
  };

  const groupedDepartments = useMemo(() => {
    const groups: Record<string, Department[]> = {};
    let classesToGroup = departmentClasses;

    if (selectedLesson) {
      classesToGroup = classesToGroup.filter((dept) => lessonClassIds.includes(dept.id));
    }

    classesToGroup.forEach((dept) => {
      const groupName = dept.parentName || "Diğer";
      if (!groups[groupName]) groups[groupName] = [];
      groups[groupName].push(dept);
    });

    return Object.keys(groups).sort().reduce((acc: Record<string, Department[]>, key) => {
      acc[key] = groups[key];
      return acc;
    }, {});
  }, [departmentClasses, selectedLesson, lessonClassIds]);

  const getClassNameForAssignment = (assignment: TeacherAssignment) => {
    if (assignment.classId && allDepartmentsMap[assignment.classId]) return allDepartmentsMap[assignment.classId];
    if (assignment.class?.name) return assignment.class.name;
    return "Sınıf bulunamadı";
  };

  const selectedTeacherData = teachers.find((teacher) => teacher.id === selectedTeacher);
  const selectedTeacherAssignedClassIds = new Set(
    (selectedTeacherData?.currentAssignments || []).map((assignment) => assignment.classId)
  );

  useEffect(() => {
    if (!selectedTeacherData) return;

    setSelectedClasses((prev) => prev.filter((classId) => !selectedTeacherAssignedClassIds.has(classId)));
  }, [selectedTeacherData]);

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="mb-6">
          <p className="text-sm text-gray-600">Önce dersi seçin, sonra o dersi verebilen öğretmenlere sınıf ataması yapın</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="lg:col-span-1">
            <div className="bg-gray-50 rounded-lg p-4 h-full flex flex-col">
              <h3 className="font-semibold text-gray-900 mb-3">Ders Seçimi</h3>
              <div className="space-y-2 flex-1 overflow-y-auto max-h-[500px]">
                {loading ? (
                  <div className="text-center p-4">
                    <Loader2 className="animate-spin mx-auto" />
                  </div>
                ) : lessons.map((lesson) => (
                  <div
                    key={lesson.id}
                    className={`p-3 rounded-lg cursor-pointer transition-colors border ${selectedLesson === lesson.id
                        ? "bg-green-100 border-green-200 text-green-800"
                        : "bg-white border-gray-200 hover:bg-gray-50"
                      }`}
                    onClick={() => setSelectedLesson((prev) => prev === lesson.id ? null : lesson.id)}
                  >
                    <div className="flex items-center space-x-2">
                      <BookOpen size={16} className="text-blue-500" />
                      <span className="font-medium text-gray-900">{lesson.name}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="bg-gray-50 rounded-lg p-4 h-full flex flex-col">
              <div className="mb-4">
                <h3 className="font-semibold text-gray-900 mb-3">Öğretmen Seçimi</h3>
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder={selectedLesson ? "Öğretmen ara..." : "Önce ders seçin"}
                    value={searchTeacher}
                    onChange={(e) => setSearchTeacher(e.target.value)}
                    disabled={!selectedLesson}
                    className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-400"
                  />
                </div>
              </div>

              <div className="space-y-2 flex-1 overflow-y-auto max-h-[500px]">
                {!selectedLesson ? (
                  <div className="text-center text-gray-400 text-sm py-10 flex flex-col items-center justify-center h-full">
                    <BookOpen size={32} className="mb-2 opacity-30" />
                    <p>Öğretmen listesi için önce</p>
                    <p className="font-medium">Ders Seçmelisiniz</p>
                  </div>
                ) : loading ? (
                  <div className="text-center p-4">
                    <Loader2 className="animate-spin mx-auto" />
                  </div>
                ) : teachers.map((teacher) => (
                  <div
                    key={teacher.id}
                    className={`p-3 rounded-lg cursor-pointer transition-colors border ${selectedTeacher === teacher.id
                        ? "bg-blue-100 border-blue-200 text-blue-800"
                        : "bg-white border-gray-200 hover:bg-gray-50"
                      }`}
                    onClick={() => setSelectedTeacher((prev) => prev === teacher.id ? null : teacher.id)}
                  >
                    <div className="flex items-center space-x-2 mb-1">
                      <GraduationCap size={16} className="text-gray-500" />
                      <span className="font-medium">{getTeacherName(teacher)}</span>
                    </div>
                    <div className="text-xs text-blue-600 ml-6 mt-1">
                      Mevcut Atama: {getTeacherAssignmentCount(teacher)}
                    </div>
                  </div>
                ))}
                {selectedLesson && !loading && teachers.length === 0 && (
                  <div className="text-center text-gray-500 text-sm py-4">Bu dersi verebilen öğretmen bulunamadı.</div>
                )}
              </div>
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="bg-gray-50 rounded-lg p-4 h-full flex flex-col">
              <h3 className="font-semibold text-gray-900 mb-3">Sınıf Seçimi</h3>
              <div className="space-y-2 flex-1 overflow-y-auto max-h-[400px]">
                {!selectedLesson ? (
                  <div className="text-center text-gray-400 text-sm py-10 flex flex-col items-center justify-center h-full">
                    <BookOpen size={32} className="mb-2 opacity-30" />
                    <p>Sınıf seçmek için önce</p>
                    <p className="font-medium">Ders Seçmelisiniz</p>
                  </div>
                ) : (
                  loading || loadingDepartments || loadingLessonClasses ? (
                    <div className="text-center p-4">
                      <Loader2 className="animate-spin mx-auto" />
                      <span className="text-xs text-gray-400 block mt-2">Sınıflar yükleniyor...</span>
                    </div>
                  ) : Object.entries(groupedDepartments).map(([deptName, deptList]) => (
                    <div key={deptName} className="mb-4">
                      <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 ml-1 sticky top-0 bg-gray-50 py-1">{deptName}</h4>
                      <div className="space-y-1">
                        {deptList.map((cls) => {
                          const isAssignedToSelectedTeacher = !!selectedTeacher && selectedTeacherAssignedClassIds.has(cls.id);

                          return (
                            <div
                              key={cls.id}
                              className={`p-3 rounded-lg transition-colors border ${isAssignedToSelectedTeacher
                                  ? "bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed"
                                  : selectedClasses.includes(cls.id)
                                    ? "bg-purple-100 border-purple-200 text-purple-800 cursor-pointer"
                                    : "bg-white border-gray-200 hover:bg-gray-50 cursor-pointer"
                                }`}
                              onClick={() => {
                                if (isAssignedToSelectedTeacher) return;
                                handleClassToggle(cls.id);
                              }}
                            >
                              <div className="flex items-center justify-between gap-2">
                                <div className="flex items-center space-x-2">
                                  <Users size={16} className={isAssignedToSelectedTeacher ? "text-gray-400" : "text-gray-500"} />
                                  <span className={`font-medium ${isAssignedToSelectedTeacher ? "text-gray-500" : ""}`}>{cls.name}</span>
                                </div>
                                {isAssignedToSelectedTeacher && (
                                  <div className="flex items-center space-x-1 text-xs text-gray-500">
                                    <Lock size={12} />
                                    <span>Atanmış</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))
                )}

                {!loading && !loadingDepartments && departmentClasses.length === 0 && (
                  <div className="text-center text-gray-500 text-sm py-4">Sınıf bulunamadı. Departman yapısını kontrol edin.</div>
                )}
              </div>

              {selectedClasses.length > 0 && (
                <div className="mt-4 p-3 bg-purple-50 rounded-lg">
                  <p className="text-sm font-medium text-purple-900">Seçili Sınıflar: {selectedClasses.length}</p>
                </div>
              )}

              <div className="mt-4">
                <button
                  onClick={createAssignment}
                  disabled={!selectedTeacher || !selectedLesson || selectedClasses.length === 0 || submitting}
                  className={`w-full flex items-center justify-center space-x-2 py-2 px-4 rounded-lg transition-colors ${selectedTeacher && selectedLesson && selectedClasses.length > 0
                      ? "bg-green-600 hover:bg-green-700 text-white"
                      : "bg-gray-300 text-gray-500 cursor-not-allowed"
                    }`}
                >
                  {submitting ? <Loader2 className="animate-spin" size={16} /> : <Plus size={16} />}
                  <span>Atama Oluştur</span>
                </button>
              </div>
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="bg-gray-50 rounded-lg p-4 h-full flex flex-col">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-semibold text-gray-900">Mevcut Atamalar</h3>
                  <p className="text-xs text-gray-500 mt-1">
                    {selectedTeacherData
                      ? `${getTeacherName(selectedTeacherData)} için mevcut atamalar`
                      : selectedLesson
                        ? "Bu dersi veren öğretmenlerin atamaları"
                        : "Tüm atamalar"}
                  </p>
                </div>
                {visibleAssignments.length > 0 && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={toggleSelectAll}
                      className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title={selectedAssignments.length === visibleAssignments.length ? "Seçimi Kaldır" : "Tümünü Seç"}
                    >
                      {selectedAssignments.length === visibleAssignments.length ? <CheckSquare size={16} /> : <Square size={16} />}
                    </button>
                    {selectedAssignments.length > 0 && (
                      <button
                        onClick={() => setBulkDeleteModalOpen(true)}
                        disabled={bulkDeleting}
                        className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                        title={`${selectedAssignments.length} atamayı sil`}
                      >
                        {bulkDeleting ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                      </button>
                    )}
                  </div>
                )}
              </div>

              <div className="space-y-3 flex-1 overflow-y-auto max-h-[500px]">
                {assignmentsLoading ? (
                  <div className="text-center p-4">
                    <Loader2 className="animate-spin mx-auto text-gray-400" />
                    <span className="text-xs text-gray-500 mt-2 block">Atamalar yükleniyor...</span>
                  </div>
                ) : visibleAssignments.map((assignment) => (
                  <div
                    key={assignment.id}
                    className={`bg-white rounded-lg p-3 border transition-colors cursor-pointer ${selectedAssignments.includes(assignment.id)
                        ? "border-blue-400 bg-blue-50"
                        : "border-gray-200 hover:border-gray-300"
                      }`}
                    onClick={() => toggleAssignmentSelection(assignment.id)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-2">
                        <div className="mt-0.5">
                          {selectedAssignments.includes(assignment.id)
                            ? <CheckSquare size={16} className="text-blue-500" />
                            : <Square size={16} className="text-gray-300" />}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            <GraduationCap size={14} className="text-blue-500" />
                            <span className="font-medium text-gray-900 text-sm">{getTeacherName(assignment.teacher)}</span>
                          </div>
                          <div className="space-y-1 ml-4">
                            <div className="flex items-center space-x-2">
                              <BookOpen size={12} className="text-gray-400" />
                              <span className="text-xs text-gray-600">{assignment.lesson?.name || "Ders Silinmiş"}</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Users size={12} className="text-gray-400" />
                              <span className="text-xs text-gray-600">{getClassNameForAssignment(assignment)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setAssignmentToDelete(assignment.id);
                          setDeleteModalOpen(true);
                        }}
                        className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Atamayı kaldır"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))}
                {!assignmentsLoading && visibleAssignments.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <GraduationCap size={48} className="mx-auto mb-3 text-gray-300" />
                    <p>Gösterilecek atama yok</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <ConfirmModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setAssignmentToDelete(null);
        }}
        onConfirm={confirmRemoveAssignment}
        title="Atama Sil"
        message="Bu atamayı silmek istediğinize emin misiniz? Bu işlem geri alınamaz."
        confirmText="Sil"
        cancelText="İptal"
        confirmButtonVariant="danger"
      />

      <ConfirmModal
        isOpen={bulkDeleteModalOpen}
        onClose={() => setBulkDeleteModalOpen(false)}
        onConfirm={confirmBulkDelete}
        title="Toplu Silme"
        message={`${selectedAssignments.length} atamayı silmek istediğinize emin misiniz? Bu işlem geri alınamaz.`}
        confirmText="Tümünü Sil"
        cancelText="İptal"
        confirmButtonVariant="danger"
        isLoading={bulkDeleting}
      />

      <ConfirmModal
        isOpen={errorModalOpen}
        onClose={() => setErrorModalOpen(false)}
        onConfirm={() => setErrorModalOpen(false)}
        title="Hata"
        message={errorMessage}
        confirmText="Tamam"
        cancelText=""
        confirmButtonVariant="primary"
      />
    </div>
  );
};

export default TeacherLessonAssignments;